import { Badge, CSSObject, Stack, Tabs, Text } from '@mantine/core'
import React, { useCallback, useEffect, useState } from 'react'
import {
	CourseAiCourse,
	CourseAiTopic,
	getCourseAiList,
	getDictationTopics,
	getSpeakingTopicPrototypes,
	getWritingTopics,
	unwrapList,
} from 'src/apis/courseAiAPI'
import { useQueryParamTab } from 'src/utils/tabQueryParam'
import { color } from 'src/variable/constant'
import LearnerManagementPanel from './LearnerManagementPanel'
import {
	COURSE_DAYS,
	DICTATION_LIST_LIMIT,
	EditableSkill,
	findSpeakingCourse,
	isCourseComplete,
	LessonCounts,
	liveLessonsOfCourse,
	matchCourseBySkill,
	SPEAKING_LIST_LIMIT,
} from './lessonCounts'
import LessonManagementPanel from './LessonManagementPanel'
import SpeakingTabPanel from './SpeakingTabPanel'

export const COURSE_AI_TABS = [
	{
		value: 'writing',
		label: 'Writing',
		enabled: true,
	},
	{
		value: 'dictation',
		label: 'Listening',
		enabled: true,
	},
	{
		value: 'pronunciation',
		label: 'Pronunciation',
		enabled: false,
	},
	{
		value: 'speaking',
		label: 'Speaking',
		// UD-671: opens when a SPEAKING course exists (see isTabEnabled), not by this flag.
		enabled: false,
	},
	{
		value: 'combo',
		label: '3-Skill Combo',
		enabled: false,
	},
] as const

export type CourseAiTabValue = (typeof COURSE_AI_TABS)[number]['value']

/** A tab is open when its flag says so; the Speaking tab opens when the Speaking course exists. */
export const isTabEnabled = (
	tab: (typeof COURSE_AI_TABS)[number],
	hasSpeakingCourse: boolean,
) => (tab.value === 'speaking' ? hasSpeakingCourse : tab.enabled)

const tabsStyles: CSSObject = {
	'.mantine-Tabs-list': {
		gap: 8,
		borderBottom: '1px solid #edf2ee',
		paddingBottom: 8,
		flexWrap: 'wrap',
	},
	'.mantine-Tabs-tab': {
		paddingInline: 14,
		paddingBlock: 10,
		color: '#374151',
		borderRadius: 8,
		fontWeight: 700,
	},
	// The selected section is drawn like the active sidebar item: brand green, white text.
	'.mantine-Tabs-tab[data-active="true"], .mantine-Tabs-tab[data-active="true"]:hover': {
		color: '#fff',
		background: color.CmsPrimary,
		borderColor: color.CmsPrimary,
	},
}

/**
 * The sections of the one Course CMS page (Admin, UD-393). Each is a tab with its
 * own panel; a new section (Pricing, UD-357, once confirmed) is one more entry here.
 */
export const COURSE_AI_SECTIONS = [
	{
		value: 'lessons',
		label: 'Manage Lessons',
		description:
			'Manage lessons for 5 AI courses, fixed Day 1 to 90 prompts, all users share the same prompt each day',
		render: () => <LessonSkillTabs />,
	},
	{
		value: 'learners',
		label: 'Manage Learners',
		description:
			'Every learner in every course, with payments, progress and free trial. Staff code accounts are listed with a label and are not counted.',
		render: () => <LearnerManagementPanel />,
	},
] as const

export type CourseAiSectionValue = (typeof COURSE_AI_SECTIONS)[number]['value']

/** The whole page is SUPERADMIN only; the server checks this too. */
const isSuperAdmin = () => {
	try {
		return JSON.parse(localStorage.getItem('info') || '{}')?.role === 'SUPERADMIN'
	} catch {
		return false
	}
}

function CourseAiPage() {
	const sectionValues = COURSE_AI_SECTIONS.map((section) => section.value)
	const { activeTab: activeSection, handleTabChange: handleSectionChange } =
		useQueryParamTab(sectionValues, 'lessons', 'section')
	const current =
		COURSE_AI_SECTIONS.find((section) => section.value === activeSection) ||
		COURSE_AI_SECTIONS[0]

	if (!isSuperAdmin()) {
		return (
			<Stack sx={{ width: '100%', padding: 14 }}>
				<Text fw={700}>AI Courses</Text>
				<Text size="sm" color="dimmed">
					This page is for super admins only.
				</Text>
			</Stack>
		)
	}

	return (
		<Stack
			sx={{
				width: '100%',
				padding: 14,
				border: '1px solid #e6ebe7',
				borderRadius: 10,
				background: '#fff',
			}}
		>
			<Stack spacing={4}>
				<Text size="xs" color="dimmed">
					AI Courses &gt; {current.label}
				</Text>
				<Text fw={700} size="xl">
					{current.label}
				</Text>
				<Text size="sm" color="dimmed">
					{current.description}
				</Text>
			</Stack>

			<Tabs
				value={activeSection}
				onTabChange={handleSectionChange}
				keepMounted={false}
				sx={tabsStyles}
			>
				<Tabs.List>
					{COURSE_AI_SECTIONS.map((section) => (
						<Tabs.Tab key={section.value} value={section.value}>
							{section.label}
						</Tabs.Tab>
					))}
				</Tabs.List>

				{COURSE_AI_SECTIONS.map((section) => (
					<Tabs.Panel key={section.value} value={section.value} pt="md">
						{section.render()}
					</Tabs.Panel>
				))}
			</Tabs>
		</Stack>
	)
}

/**
 * How many lessons each skill's course really holds (UD-619, UD-493). Read once for the tabs the
 * staff is not looking at, and kept current by the open panel each time it reads its list.
 */
export const useLessonCounts = () => {
	const [counts, setCounts] = useState<LessonCounts>({})
	const [speakingCourseId, setSpeakingCourseId] = useState('')

	useEffect(() => {
		let cancelled = false
		const load = async () => {
			let courses: CourseAiCourse[]
			try {
				courses = unwrapList(
					await getCourseAiList({ limit: 100, offset: 0 }),
				).rows as CourseAiCourse[]
			} catch {
				// No number is better than a wrong one: the panel reports the error itself.
				return
			}
			const speakingCourse = findSpeakingCourse(courses)
			if (!cancelled) setSpeakingCourseId(speakingCourse?.id || '')
			const skills: EditableSkill[] = ['writing', 'dictation']
			if (speakingCourse) skills.push('speaking')
			const found: LessonCounts = {}
			await Promise.all(
				skills.map(async (skill) => {
					const course = matchCourseBySkill(courses, skill)
					if (!course) return
					try {
						const response =
							skill === 'writing'
								? await getWritingTopics({ course_id: course.id })
								: skill === 'speaking'
									? await getSpeakingTopicPrototypes({
											course_id: course.id,
											limit: SPEAKING_LIST_LIMIT,
										})
									: await getDictationTopics({
											course_id: course.id,
											limit: DICTATION_LIST_LIMIT,
										})
						found[skill] = liveLessonsOfCourse(
							unwrapList(response).rows as CourseAiTopic[],
							course.id,
						).length
					} catch {
						// This tab shows no badge.
					}
				}),
			)
			// A number the open panel already reported is newer than this one.
			if (!cancelled) setCounts((previous) => ({ ...found, ...previous }))
		}
		load()
		return () => {
			cancelled = true
		}
	}, [])

	const report = useCallback((skill: EditableSkill, count: number) => {
		setCounts((previous) =>
			previous[skill] === count ? previous : { ...previous, [skill]: count },
		)
	}, [])

	return { counts, report, hasSpeakingCourse: Boolean(speakingCourseId), speakingCourseId }
}

/** "30 / 90 lessons": the real number, and the 90 a Day 1 to 90 course needs to be complete. */
export function LessonCountBadge({ count }: { count?: number }) {
	if (count === undefined) return null
	const complete = isCourseComplete(count)
	return (
		<Badge
			size="sm"
			variant="light"
			color={complete ? 'green' : 'yellow'}
			title={
				complete
					? `${count} lessons: the ${COURSE_DAYS} day course is complete`
					: `${count} of ${COURSE_DAYS} lessons: the ${COURSE_DAYS} day course is complete with ${COURSE_DAYS} or more`
			}
		>
			{count} / {COURSE_DAYS} lessons
		</Badge>
	)
}

/** Lam's lesson editor, one tab per skill (unchanged, moved under Manage Lessons). */
function LessonSkillTabs() {
	const tabValues = COURSE_AI_TABS.map((tab) => tab.value)
	const { activeTab, handleTabChange } = useQueryParamTab(
		tabValues,
		'writing',
	)
	const { counts, report, hasSpeakingCourse, speakingCourseId } = useLessonCounts()

	return (
			<Tabs
				value={activeTab}
				onTabChange={handleTabChange}
				keepMounted={false}
				sx={{
					'.mantine-Tabs-list': {
						gap: 8,
						borderBottom: '1px solid #edf2ee',
						paddingBottom: 8,
						flexWrap: 'wrap',
					},
					'.mantine-Tabs-tab': {
						paddingInline: 14,
						paddingBlock: 10,
						color: '#374151',
						borderRadius: 8,
						fontWeight: 700,
					},
					'.mantine-Tabs-tab[data-active="true"]': {
						color: '#1f7a44',
						background: '#ecfdf3',
					},
				}}
			>
				<Tabs.List>
					{COURSE_AI_TABS.map((tab) => (
						<Tabs.Tab
							key={tab.value}
							value={tab.value}
							disabled={!isTabEnabled(tab, hasSpeakingCourse)}
							rightSection={
								// A tab that is not open yet has no lessons to count, so it shows no number.
								isTabEnabled(tab, hasSpeakingCourse) ? (
									<LessonCountBadge
										count={counts[tab.value as EditableSkill]}
									/>
								) : undefined
							}
						>
							{tab.label}
						</Tabs.Tab>
					))}
				</Tabs.List>

				{COURSE_AI_TABS.map((tab) => (
					<Tabs.Panel key={tab.value} value={tab.value} pt="md">
						{tab.value === 'speaking' && isTabEnabled(tab, hasSpeakingCourse) ? (
							<SpeakingTabPanel courseId={speakingCourseId} onLessonCount={report} />
						) : isTabEnabled(tab, hasSpeakingCourse) ? (
							<LessonManagementPanel
								skill={tab.value}
								onLessonCount={report}
							/>
						) : (
							<Text color="dimmed" size="sm">
								Coming soon — CMS API for {tab.label} will be added in
								Phase 2.
							</Text>
						)}
					</Tabs.Panel>
				))}
			</Tabs>
	)
}

export default CourseAiPage
